import React, { useState, useRef, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import SignatureCanvas from 'react-signature-canvas';
import {
  convertPendingToCompleted,
  savePendingChecklistToFirestore,
  getPendingChecklistFromFirestore,
  calculateChecklistStats,
  uploadCategoryPhotos,
  getChecklistTemplate,
  searchVehiclesByPlateNo,
  getRegistrationStatus,
  generatePDFSummary,
  deleteChecklistPhoto,
  type ChecklistItem,
  type ChecklistCategory,
  type Vehicle,
  type PDFGenerationParams,
  type PreDepartureChecklistData
} from '../utils/checklistUtils';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  IconButton,
  Card,
  CardBody,
  Button,
  Flex,
  Badge,
  useToast,
  useColorModeValue,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Image,
  Textarea,
  Radio,
  RadioGroup,
  Stack,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepSeparator,
  useSteps,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox
} from '@chakra-ui/react';
import { FiCamera, FiPlus, FiTrash2, FiImage, FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import PageHeader from './shared/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTimeDisplay } from '../utils/formatDateTime';
import { getPhotoUrl } from '../utils/misc';

// Photo compression utility
const compressImage = (file: File, maxWidth: number = 800, quality: number = 0.7): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxWidth) {
          width = (width * maxWidth) / height;
          height = maxWidth;
        }
      }
      
      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          }
        },
        'image/jpeg',
        quality
      );
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Photo Display Component with async URL loading
const PhotoDisplay: React.FC<{
  photoStoragePath?: string;
  photo?: string;
  alt: string;
  onRemove: () => void;
}> = ({ photoStoragePath, photo, alt, onRemove }) => {
  const [photoUrl, setPhotoUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (photoStoragePath) {
      setIsLoading(true);
      setError('');
      
      getPhotoUrl(photoStoragePath)
        .then((url:any) => {
          setPhotoUrl(url);
          setIsLoading(false);
        })
        .catch((err:any) => {
          console.error('Error loading photo URL:', err);
          setError('Failed to load photo');
          setIsLoading(false);
        });
    } else if (photo) {
      setPhotoUrl(photo);
      setIsLoading(false);
      setError('');
    } else {
      setPhotoUrl('');
      setIsLoading(false);
      setError('');
    }
  }, [photoStoragePath, photo]);

  if (!photoStoragePath && !photo) {
    return null;
  }

  if (isLoading) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        minH="100px" 
        bg="gray.100" 
        borderRadius="md"
      >
        <Text fontSize="sm" color="gray.600">Loading photo...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        minH="100px" 
        bg="red.50" 
        borderRadius="md"
        border="1px"
        borderColor="red.200"
      >
        <Text fontSize="sm" color="red.600">Failed to load photo</Text>
      </Box>
    );
  }

  if (photoUrl) {
    return (
      <Box position="relative">
        <Image 
          src={photoUrl} 
          alt={alt}
          maxH="300px"
          w="100%"
          objectFit="contain"
          borderRadius="md"
          border="1px"
          borderColor="gray.200"
        />
        <IconButton
          aria-label="Remove photo"
          icon={<FiTrash2 />}
          size="sm"
          colorScheme="red"
          variant="solid"
          position="absolute"
          top="2"
          right="2"
          onClick={onRemove}
          _hover={{
            backgroundColor: 'red.600'
          }}
        />
      </Box>
    );
  }

  return null;
};

interface PerformChecklistProps {
  onBack: () => void;
}

const PerformChecklist: React.FC<PerformChecklistProps> = ({ onBack }) => {
  const { selectedCompany, userProfile } = useAuth();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const pendingToastShownRef = useRef<string>(''); // Track which vehicle we've shown pending toast for
//   const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isAddItemOpen,
    onOpen: onAddItemOpen,
    onClose: onAddItemClose
  } = useDisclosure();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [newItemText, setNewItemText] = useState('');
  const [checklistData, setChecklistData] = useState<PreDepartureChecklistData | null>(null);
  
  // Vehicle search and selection state
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [isSearchingVehicles, setIsSearchingVehicles] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isUploadingCategoryPhotos, setIsUploadingCategoryPhotos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpeningSummary, setIsOpeningSummary] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{[stepIndex: number]: {
    hasPhotos: boolean;
    uploadedCount: number;
    failedCount: number;
    totalCount: number;
    failedItems: string[];
  }}>({});
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);

  // Summary modal
  const {
    isOpen: isSummaryOpen,
    onOpen: onSummaryOpen,
    onClose: onSummaryClose
  } = useDisclosure();

  // Final assessment modal
  const {
    isOpen: isFinalAssessmentOpen,
    onOpen: onFinalAssessmentOpen,
    onClose: onFinalAssessmentClose
  } = useDisclosure();

  // Final assessment state
  const [overallStatus, setOverallStatus] = useState<'pass' | 'fail' | ''>('');
  const [inspectionSummary, setInspectionSummary] = useState('');
  const [signatureDataURL, setSignatureDataURL] = useState<string>('');
  const [isSigningMode, setIsSigningMode] = useState<boolean>(false);

  // Vehicle search functionality
  const handleVehicleSearch = async () => {
    if (!vehicleSearchQuery.trim()) {
      toast({
        title: 'Search Query Required',
        description: 'Please enter a plate number to search.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (vehicleSearchQuery.trim().length < 3) {
      toast({
        title: 'Search Query Too Short',
        description: 'Please enter at least 3 characters to search.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSearchingVehicles(true);
      const results = await searchVehiclesByPlateNo(
        vehicleSearchQuery.trim(), selectedCompany?.companyId
      );
      setSearchResults(results);
      setHasSearched(true);
      
      if (results.length === 0) {
        toast({
          title: 'No Vehicles Found',
          description: 'No active vehicles found matching your search.',
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error searching vehicles:', error);
      toast({
        title: 'Search Failed',
        description: 'Failed to search vehicles. Please try again.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSearchingVehicles(false);
    }
  };

  const handleVehicleSelect = async (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    
    try {
      // Try to load existing pending checklist for this vehicle
      const existingPending = await getPendingChecklistFromFirestore(vehicle.plateNo, selectedCompany?.companyId || '');
      
      if (existingPending) {
        // Load the existing pending checklist data
        setCategories(existingPending.categories);
        setOverallStatus(existingPending.overallStatus || '');
        setInspectionSummary(existingPending.inspectionSummary || '');
        setSignatureDataURL(existingPending.signatureDataURL || '');
        // setPendingChecklistId(existingPending.customId); // Set the ID from existing checklist
        setHasLoadedFromPending(true); // Prevent template reload
        
        // Only show toast if we haven't already shown it for this vehicle
        if (pendingToastShownRef.current !== vehicle.plateNo) {
          pendingToastShownRef.current = vehicle.plateNo;
          toast({
            title: 'Pending Checklist Loaded',
            description: `Found existing ${existingPending.status.toUpperCase()} checklist for this vehicle. You can continue where you left off.`,
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
        }
      } else {
        setHasLoadedFromPending(false); // Allow template loading for new checklists
        pendingToastShownRef.current = ''; // Reset ref for new checklist
      }
    } catch (error) {
      console.error('Error loading pending checklist:', error);
      setHasLoadedFromPending(false); // Allow template loading on error
      pendingToastShownRef.current = ''; // Reset ref on error
      // Continue without loading - user can start fresh
    }
  };

  const clearVehicleSelection = () => {
    setSelectedVehicle(null);
    setVehicleSearchQuery('');
    setSearchResults([]);
    setHasSearched(false);
    // setPendingChecklistId('');
    setHasLoadedFromPending(false); // Reset flag to allow template loading
    pendingToastShownRef.current = ''; // Reset toast ref
    
    // Reset checklist data when clearing vehicle selection
    setOverallStatus('');
    setInspectionSummary('');
    setSignatureDataURL('');
    setUploadStatus({});
  };

  const [categories, setCategories] = useState<ChecklistCategory[]>([]);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);
  const [hasLoadedFromPending, setHasLoadedFromPending] = useState(false);
  const [photosToDelete, setPhotosToDelete] = useState<string[]>([]); // Track storage paths of photos to delete

  // Load checklist template from database
  useEffect(() => {
    const loadChecklistTemplate = async () => {
      if (!selectedCompany?.companyId) return;
      
      // Don't reload template if we just loaded from pending checklist
      if (hasLoadedFromPending) {
        setIsLoadingTemplate(false);
        return;
      }
      
      try {
        setIsLoadingTemplate(true);
        const template = await getChecklistTemplate(selectedCompany.companyId);

        if (template) {
          setCategories(template.categories);
        } else {
          // Only show toast if we haven't loaded from pending (to avoid multiple toasts)
          if (!hasLoadedFromPending) {
            toast({
              title: 'Template Not Found',
              description: 'No checklist template found for your company. Please contact administrator.',
              status: 'warning',
              duration: 2000,
              isClosable: true,
            });
          }
          setCategories([]);
        }
      } catch (error) {
        console.error('Error loading checklist template:', error);
        // Only show toast if we haven't loaded from pending (to avoid multiple toasts)
        if (!hasLoadedFromPending) {
          toast({
            title: 'Error Loading Template',
            description: 'Failed to load checklist template. Please try refreshing the page.',
            status: 'error',
            duration: 2000,
            isClosable: true,
          });
        }
        setCategories([]);
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    loadChecklistTemplate();
  }, [selectedCompany?.companyId, hasLoadedFromPending]);

  // Wizard stepper state - depends on categories being loaded
  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: categories.length + 1, // +1 for vehicle selection step
  });

  // Reset step when categories change to prevent out-of-bounds issues
  useEffect(() => {
    if (categories.length > 0) {
      setCurrentStep(0);
      setActiveStep(0);
    }
  }, [categories.length, setActiveStep]);

  // Prevent keyboard popup on signature canvas (Android fix)
  useEffect(() => {
    const preventKeyboard = () => {
      // Prevent zoom and keyboard on signature canvas
      const canvas = signatureRef.current?.getCanvas();
      if (canvas) {
        canvas.addEventListener('touchstart', (e) => {
          e.preventDefault();
        }, { passive: false });
        
        canvas.addEventListener('focus', (e) => {
          const target = e.target as HTMLElement;
          if (target && target.blur) {
            target.blur();
          }
        });
        
        // Prevent default touch behaviors that might trigger keyboard
        canvas.style.touchAction = 'none';
        canvas.setAttribute('readonly', 'true');
        canvas.setAttribute('inputmode', 'none');
        
        // iOS-specific fixes
        canvas.style.userSelect = 'none';
        canvas.style.setProperty('-webkit-user-select', 'none');
        canvas.style.setProperty('-webkit-touch-callout', 'none');
        canvas.style.setProperty('-webkit-tap-highlight-color', 'transparent');
        canvas.setAttribute('autocomplete', 'off');
        canvas.setAttribute('autocorrect', 'off');
        canvas.setAttribute('autocapitalize', 'off');
        canvas.setAttribute('spellcheck', 'false');
      }
    };

    const handleDocumentTouchStart = (e: TouchEvent) => {
      // If touching the signature canvas area, prevent any focus/keyboard events
      const canvas = signatureRef.current?.getCanvas();
      if (canvas && e.target === canvas) {
        e.preventDefault();
        
        // iOS: Prevent zoom on double tap
        const now = Date.now();
        const timeSinceLastTouch = now - (canvas as any).lastTouchTime || 0;
        if (timeSinceLastTouch < 300) {
          e.preventDefault(); // Prevent double-tap zoom
        }
        (canvas as any).lastTouchTime = now;
        
        // Blur any active input to ensure keyboard doesn't popup
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    };

    if (isSigningMode && signatureRef.current) {
      // iOS: Temporarily modify viewport to prevent zoom during signing
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      let originalViewport: string | null = null;
      
      if (isIOS) {
        const viewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
        if (viewport) {
          originalViewport = viewport.content;
          viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
        }
      }
      
      // Small delay to ensure canvas is mounted
      setTimeout(preventKeyboard, 100);
      
      // Add document-level touch handler
      document.addEventListener('touchstart', handleDocumentTouchStart, { passive: false });
      
      return () => {
        document.removeEventListener('touchstart', handleDocumentTouchStart);
        
        // iOS: Restore original viewport
        if (isIOS && originalViewport) {
          const viewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
          if (viewport) {
            viewport.content = originalViewport;
          }
        }
      };
    }
  }, [isSigningMode]);

  // Calculate progress based on status for wizard completion
  const stats = calculateChecklistStats(categories);
  const { totalRequired, completedRequired, isComplete: canComplete } = stats;

  // Check if all summary fields are complete
  const isSummaryComplete = overallStatus && inspectionSummary.trim() && signatureDataURL;
  const isFullyComplete = canComplete && isSummaryComplete;

  // Auto-save to pending checklist when data changes
  // Auto-save functionality removed to optimize bandwidth and Firestore write costs
  // Data is only saved when user navigates between steps
  
  // Debug logging for completion status
  useEffect(() => {
    console.log('Completion Debug:', {
      canComplete,
      totalRequired,
      completedRequired,
      isSummaryComplete,
      overallStatus: !!overallStatus,
      inspectionSummary: !!inspectionSummary.trim(),
      signatureDataURL: !!signatureDataURL,
      isFullyComplete
    });
  }, [canComplete, totalRequired, completedRequired, isSummaryComplete, overallStatus, inspectionSummary, signatureDataURL, isFullyComplete]);

  const handleStatusChange = (categoryId: string, itemId: string, status: 'green' | 'yellow' | 'red') => {
    setCategories(prev =>
      prev.map(category =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.map(item =>
                item.id === itemId 
                  ? { 
                      ...item, 
                      status,
                      checked: true // Set checked to true when any status is selected
                    } 
                  : item
              )
            }
          : category
      )
    );
  };

  const handleCommentChange = (categoryId: string, itemId: string, comment: string) => {
    setCategories(prev =>
      prev.map(category =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.map(item =>
                item.id === itemId ? { ...item, comment } : item
              )
            }
          : category
      )
    );
  };

  const handlePhotoUpload = (categoryId: string, itemId: string) => {
    setSelectedCategoryId(categoryId);
    setSelectedItemId(itemId);
    fileInputRef.current?.click();
  };

  // Helper function to delete marked photos from Firebase Storage
  const deleteMarkedPhotos = async () => {
    if (photosToDelete.length === 0) return;

    try {
      // Delete photos in parallel
      await Promise.all(
        photosToDelete.map(async (storagePath) => {
          try {
            await deleteChecklistPhoto(storagePath);
          } catch (error) {
            console.error(`Failed to delete photo at ${storagePath}:`, error);
            // Continue with other deletions even if one fails
          }
        })
      );

      // Clear the deletion queue after successful deletions
      setPhotosToDelete([]);
    } catch (error) {
      console.error('Error deleting marked photos:', error);
      // Don't throw error - continue with the process even if deletion fails
    }
  };

  const handleRemovePhoto = (categoryId: string, itemId: string) => {
    setCategories(prev => 
      prev.map(category => 
        category.id === categoryId
          ? {
              ...category,
              items: category.items.map(item => {
                if (item.id === itemId) {
                  // If there's a photoStoragePath, mark it for deletion
                  if (item.photoStoragePath) {
                    setPhotosToDelete(prevToDelete => [...prevToDelete, item.photoStoragePath!]);
                  }
                  
                  return { 
                    ...item, 
                    photo: undefined, 
                    photoFile: undefined, 
                    photoStoragePath: undefined 
                  };
                }
                return item;
              })
            }
          : category
      )
    );
    
    toast({
      title: 'Photo Removed',
      description: 'Photo has been removed from this item.',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select a file smaller than 10MB',
        status: 'error',
        duration:2000,
        isClosable: true,
      });
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsUploadingPhoto(true);
      
      // Show loading toast
      const loadingToast = toast({
        title: 'Processing image...',
        description: 'Compressing and preparing photo for upload',
        status: 'info',
        duration: null,
        isClosable: false,
      });

      // Compress the image
      const compressedFile = await compressImage(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        let photoUrl = e.target?.result as string;
        
        // Ensure the data URL has the correct JPEG format since we compressed to JPEG
        if (photoUrl && !photoUrl.startsWith('data:image/jpeg')) {
          // Extract the base64 data and recreate with correct JPEG header
          const base64Data = photoUrl.split(',')[1];
          photoUrl = `data:image/jpeg;base64,${base64Data}`;
          console.log('Fixed photo URL format to JPEG');
        }
        
        console.log('Photo URL created:', photoUrl.substring(0, 50));
        
        setCategories(prev =>
          prev.map(category =>
            category.id === selectedCategoryId
              ? {
                  ...category,
                  items: category.items.map(item =>
                    item.id === selectedItemId
                      ? { 
                          ...item, 
                          photo: photoUrl, 
                          photoFile: compressedFile // This will be uploaded to Firebase Storage when submitting
                        }
                      : item
                  )
                }
              : category
          )
        );

        // Close loading toast
        toast.close(loadingToast);
        setIsUploadingPhoto(false);
        
        toast({
          title: 'Photo prepared',
          description: `Photo compressed from ${(file.size / 1024 / 1024).toFixed(1)}MB to ${(compressedFile.size / 1024 / 1024).toFixed(1)}MB. Will be uploaded when checklist is submitted.`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      };

      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error compressing image:', error);
      setIsUploadingPhoto(false);
      toast({
        title: 'Error',
        description: 'Failed to process image. Please try again.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }

    event.target.value = '';
  };

  const handleAddNewItem = () => {
    if (!newItemText.trim() || !selectedCategoryId) return;

    const newItem: ChecklistItem = {
      id: `custom_${Date.now()}`,
      text: newItemText.trim(),
      checked: false,
      required: false,
      status: '',
      comment: ''
    };

    setCategories(prev =>
      prev.map(category =>
        category.id === selectedCategoryId
          ? { ...category, items: [...category.items, newItem] }
          : category
      )
    );

    setNewItemText('');
    onAddItemClose();

    toast({
      title: 'Item added',
      description: 'New checklist item has been added',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleRemoveItem = (categoryId: string, itemId: string) => {
    setCategories(prev =>
      prev.map(category =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.filter(item => item.id !== itemId)
            }
          : category
      )
    );

    toast({
      title: 'Item removed',
      description: 'Checklist item has been removed',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleCompleteChecklist = async () => {
    // Check if vehicle is selected
    if (!selectedVehicle) {
      toast({
        title: 'Vehicle Required',
        description: 'Please select a vehicle before submitting.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!canComplete) {
      toast({
        title: 'Incomplete Checklist',
        description: 'Please complete all required items before proceeding.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // Check if final assessment is complete
    if (!overallStatus) {
      toast({
        title: 'Overall Status Required',
        description: 'Please provide an overall vehicle status assessment.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!inspectionSummary.trim()) {
      toast({
        title: 'Inspection Summary Required',
        description: 'Please provide an inspection summary.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!signatureDataURL) {
      toast({
        title: 'Signature Required',
        description: 'Please provide your digital signature to complete the inspection.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // Proceed with submission since assessment is already completed
    await handleFinalSubmit();
  };

  const handleFinalAssessmentComplete = () => {
    // Validate final assessment
    if (!overallStatus) {
      toast({
        title: 'Assessment Required',
        description: 'Please select an overall assessment before continuing.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!inspectionSummary.trim()) {
      toast({
        title: 'Summary Required',
        description: 'Please provide an inspection summary before continuing.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!signatureDataURL) {
      toast({
        title: 'Signature Required',
        description: 'Please provide your digital signature before continuing.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // Close final assessment modal and show summary
    onFinalAssessmentClose();
    onSummaryOpen();
  };

  // Signature handling functions
  const handleSignatureSave = () => {
    if (signatureRef.current) {
      const dataURL = signatureRef.current.toDataURL('image/png');
      setSignatureDataURL(dataURL);
      setIsSigningMode(false);
    //   toast({
    //     title: 'Signature Saved',
    //     description: 'Your signature has been captured successfully.',
    //     status: 'success',
    //     duration: 2000,
    //     isClosable: true,
    //   });
    }
  };

  const handleSignatureClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    setSignatureDataURL('');
    setIsSigningMode(true);
    
    // Re-blur any active elements to prevent keyboard
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    
    toast({
      title: 'Signature Cleared',
      description: 'You can now sign again.',
      status: 'info',
      duration: 1500,
      isClosable: true,
    });
  };

  const handleFinalSubmit = async () => {
    // Set submitting state to disable buttons
    setIsSubmitting(true);

    // Show loading state
    const loadingToast = toast({
      title: 'Submitting Checklist...',
      description: 'Finalizing checklist submission',
      status: 'info',
      duration: null,
      isClosable: false,
    });

    try {
      // Calculate stats
      const stats = calculateChecklistStats(categories);
      
      // Get current timestamp
      const currentTimestamp = Timestamp.now();
      
      // Prepare checklist data for Firestore with new field names
      const checklistData = {
        plateNumber: selectedVehicle?.plateNo || '',
        vehicleId: selectedVehicle?.id || '',
        companyId: selectedCompany?.companyId || '',
        companyName: selectedCompany?.companyName || '',
        completed_by: userProfile?.email || 'unknown-user',
        updated_by: userProfile?.email || 'unknown-user',
        created_at: currentTimestamp,
        updated_at: currentTimestamp,
        completed_at: currentTimestamp,
        categories: categories,
        totalRequired: stats.totalRequired,
        completedRequired: stats.completedRequired,
        isComplete: stats.isComplete,
        overallStatus: overallStatus || undefined, // Convert empty string to undefined
        inspectionSummary: inspectionSummary.trim() || undefined, // Convert empty string to undefined
        signatureDataURL: signatureDataURL || undefined, // Include digital signature
        location: 'Current Location', // You can add geolocation here
        notes: '',
        wayBillNumber: '' // Empty value - will be linked to delivery event in admin
      };

      // Delete any marked photos before submitting
      await deleteMarkedPhotos();

      // Convert pending checklist to completed checklist (this handles photo movement and cleanup)
      const customId = await convertPendingToCompleted(
        selectedVehicle?.plateNo || '', 
        selectedCompany?.companyId || '', 
        checklistData
      );
      
      // Close loading toast
      toast.close(loadingToast);
      
      toast({
        title: 'Checklist Submitted Successfully',
        description: `Checklist saved with ID: ${customId}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      // Close summary modal and final assessment modal
      onSummaryClose();
      onFinalAssessmentClose();

      // Reset checklist for next use
      setCategories(prev =>
        prev.map(category => ({
          ...category,
          items: category.items.map(item => ({ 
            ...item, 
            checked: false,
            status: '',
            comment: '',
            photo: undefined, 
            photoFile: undefined,
            photoStoragePath: undefined
          }))
        }))
      );

      // Reset other states
      clearVehicleSelection();
      setCurrentStep(0);
      setActiveStep(0);
      // setPendingChecklistId('');
      setOverallStatus('');
      setInspectionSummary('');
      setSignatureDataURL('');
      setUploadStatus({});
      setPhotosToDelete([]); // Clear photos to delete queue

    } catch (error) {
      // Close loading toast
      toast.close(loadingToast);
      
      console.error('Error submitting checklist:', error);
      toast({
        title: 'Submission Failed',
        description: 'Failed to submit checklist. Please check your internet connection and try again.',
        status: 'error',
        duration:2000,
        isClosable: true,
      });
    } finally {
      // Always reset submitting state
      setIsSubmitting(false);
    }
  };

  // Scroll to top of main content when changing steps
  const scrollToMainContent = () => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  // Save current progress when user navigates between steps (cost-optimized)
  const saveCurrentProgress = async () => {
    if (!selectedVehicle) return;
    
    try {
      // Calculate stats for entire checklist
      const stats = calculateChecklistStats(categories);
      
      // Get current timestamp
      const currentTimestamp = Timestamp.now();
      
      // Prepare checklist data (full data as we maintain one record per vehicle)
      const checklistData = {
        plateNumber: selectedVehicle.plateNo,
        vehicleId: selectedVehicle.id,
        companyId: selectedCompany?.companyId || '',
        companyName: selectedCompany?.companyName || '',
        completed_by: userProfile?.email || 'unknown-user',
        updated_by: userProfile?.email || 'unknown-user',
        created_at: currentTimestamp,
        updated_at: currentTimestamp,
        categories: categories,
        totalRequired: stats.totalRequired,
        completedRequired: stats.completedRequired,
        isComplete: stats.isComplete,
        overallStatus: overallStatus || undefined,
        inspectionSummary: inspectionSummary.trim() || undefined,
        signatureDataURL: signatureDataURL || undefined,
        location: 'Current Location',
        notes: '',
        wayBillNumber: ''
      };
      
      // Save to pending
      const data = await savePendingChecklistToFirestore(
        checklistData, 
        selectedVehicle.plateNo, 
        selectedCompany?.companyId || ''
      );
      
      // Update the pending checklist ID
      // setPendingChecklistId(savedId);
      
      console.log('Saved progress on step navigation:', selectedVehicle.plateNo);
      setChecklistData(data)
      return data
    } catch (error) {
      console.error('Error saving progress on step navigation:', error);
      // Show error for step navigation saves as user expects this to work
      toast({
        title: 'Save Failed',
        description: 'Could not save progress. Please check your connection.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Wizard navigation functions
  const handleNextStep = async () => {
    // If this is the final step (Complete button), set loading state immediately
    if (currentStep === categories.length) {
      setIsOpeningSummary(true);
    }

    try {
      // Step 0: Vehicle Selection
      if (currentStep === 0) {
        if (!selectedVehicle) {
          toast({
            title: 'Vehicle Required',
            description: 'Please select a vehicle before proceeding.',
            status: 'warning',
            duration: 2000,
            isClosable: true,
          });
          return;
        }
        
        // Move to first checklist category
        setCurrentStep(1);
        setActiveStep(1);
        // Scroll to top of main content
        setTimeout(() => scrollToMainContent(), 100);
        return;
      }

      // Save current progress before moving to next step
      const checklistData = await saveCurrentProgress();

    // Checklist steps (1 onwards)

    // Delete marked photos from Firebase Storage before uploading new ones
    await deleteMarkedPhotos();

    // Upload photos for current category if there are any pending uploads
    const currentCategory = getCurrentCategory();
    if (!currentCategory) return; // Safety check
    
    const hasPhotosToUpload = currentCategory.items.some(item => item.photoFile);
    
    if (hasPhotosToUpload) {
      setIsUploadingCategoryPhotos(true);
      
      const uploadingToast = toast({
        title: 'Uploading Photos...',
        description: `Uploading photos for ${currentCategory.name}`,
        status: 'info',
        duration: null,
        isClosable: false,
      });

      try {
        // Upload photos for current category
        if (!selectedVehicle) return; // Safety check
        const uploadResult = await uploadCategoryPhotos(checklistData?.customId , selectedCompany?.companyId || '', selectedVehicle.plateNo, currentCategory);
        
        // Update the categories state with uploaded photos
        setCategories(prev =>
          prev.map(category =>
            category.id === currentCategory.id ? uploadResult.updatedCategory : category
          )
        );

        // Update upload status tracking
        setUploadStatus(prev => ({
          ...prev,
          [currentStep]: {
            hasPhotos: uploadResult.totalCount > 0,
            uploadedCount: uploadResult.uploadedCount,
            failedCount: uploadResult.failedCount,
            totalCount: uploadResult.totalCount,
            failedItems: uploadResult.failedItems
          }
        }));

        toast.close(uploadingToast);
        
        if (uploadResult.failedCount > 0) {
          toast({
            title: 'Partial Upload Success',
            description: `${uploadResult.uploadedCount} photos uploaded, ${uploadResult.failedCount} failed. Failed items: ${uploadResult.failedItems.join(', ')}`,
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Photos Uploaded',
            description: `All ${uploadResult.uploadedCount} photos for ${currentCategory.name} uploaded successfully`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        toast.close(uploadingToast);
        console.error('Error uploading category photos:', error);
        toast({
          title: 'Upload Failed',
          description: 'Some photos failed to upload. You can try again or continue.',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
        // Continue even if upload fails - user can retry later
      } finally {
        setIsUploadingCategoryPhotos(false);
      }
    }

    // Move to next step or show final assessment
    if (currentStep < categories.length) {
      setCurrentStep(currentStep + 1);
      setActiveStep(currentStep + 1);
      // Scroll to top of main content
      setTimeout(() => scrollToMainContent(), 100);
    } else {
      // Show final assessment modal before summary
      // Initialize signing mode if no signature exists
      if (!signatureDataURL) {
        setIsSigningMode(true);
      }
      
      // Small delay to show loading state, then open modal
      setTimeout(() => {
        onFinalAssessmentOpen();
        setIsOpeningSummary(false);
      }, 500);
    }
    } catch (error) {
      console.error('Error in handleNextStep:', error);
      // Reset loading state if there's an error
      if (currentStep === categories.length) {
        setIsOpeningSummary(false);
      }
      toast({
        title: 'Error',
        description: 'An error occurred while processing. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePrevStep = async () => {
    if (currentStep > 0) {
      // Save current progress before navigating
      const docId = await saveCurrentProgress() || '';
      
      // If going back to vehicle selection (step 0), no photo upload needed
      if (currentStep === 1) {
        setCurrentStep(0);
        setActiveStep(0);
        // Scroll to top of main content
        setTimeout(() => scrollToMainContent(), 100);
        return;
      }
      
      // Upload photos for current category before going back (if any)
      const currentCategory = getCurrentCategory();
      if (!currentCategory) return; // Safety check
      
      const hasPhotosToUpload = currentCategory.items.some(item => item.photoFile);
      
      if (hasPhotosToUpload && selectedVehicle) {
        setIsUploadingCategoryPhotos(true);
        
        const uploadingToast = toast({
          title: 'Saving Photos...',
          description: `Saving photos for ${currentCategory.name}`,
          status: 'info',
          duration: null,
          isClosable: false,
        });

        try {
          const uploadResult = await uploadCategoryPhotos(docId, selectedCompany?.companyId || '', selectedVehicle.plateNo, currentCategory);
          
          setCategories(prev =>
            prev.map(category =>
              category.id === currentCategory.id ? uploadResult.updatedCategory : category
            )
          );

          // Update upload status
          setUploadStatus(prev => ({
            ...prev,
            [currentStep]: {
              hasPhotos: uploadResult.totalCount > 0,
              uploadedCount: uploadResult.uploadedCount,
              failedCount: uploadResult.failedCount,
              totalCount: uploadResult.totalCount,
              failedItems: uploadResult.failedItems
            }
          }));

          toast.close(uploadingToast);
          
          if (uploadResult.failedCount > 0) {
            toast({
              title: 'Some Photos Not Saved',
              description: `${uploadResult.failedCount} photos failed to save. You can retry later.`,
              status: 'warning',
              duration: 2000,
              isClosable: true,
            });
          }
        } catch (error) {
          toast.close(uploadingToast);
          console.error('Error uploading category photos:', error);
          // Continue even if upload fails
        } finally {
          setIsUploadingCategoryPhotos(false);
        }
      }

      setCurrentStep(currentStep - 1);
      setActiveStep(currentStep - 1);
      // Scroll to top of main content
      setTimeout(() => scrollToMainContent(), 100);
    }
  };

  const getCurrentCategory = () => {
    // Step 0 is vehicle selection, checklist categories start from step 1
    if (currentStep === 0) return null;
    return categories[currentStep - 1] || null;
  };
  
  const isCurrentCategoryComplete = () => {
    // Step 0 (vehicle selection) is complete if vehicle is selected
    if (currentStep === 0) return !!selectedVehicle;
    
    const category = getCurrentCategory();
    if (!category) return false;
    const requiredItems = category.items.filter(item => item.required);
    const completedRequired = requiredItems.filter(item => item.status !== undefined && item.status !== '');
    
    // Check if all required items are completed
    const requiredItemsComplete = completedRequired.length === requiredItems.length;
    
    // Check if all items with "red" status have photos (regardless of whether they're required)
    const itemsWithRedStatus = category.items.filter(item => item.status === 'red');
    const redItemsWithPhotos = itemsWithRedStatus.filter(item => item.photo || item.photoFile || item.photoStoragePath);
    const redItemsComplete = itemsWithRedStatus.length === 0 || redItemsWithPhotos.length === itemsWithRedStatus.length;
    
    return requiredItemsComplete && redItemsComplete;
  };

  // Retry failed uploads for a specific step
  const retryFailedUploads = async (stepIndex: number) => {
    if (!selectedVehicle) return;
    
    // stepIndex here is currentStep (1-based), so convert to category index (0-based)
    const categoryIndex = stepIndex - 1;
    const category = categories[categoryIndex];
    
    if (!category) return;
    
    const hasPhotosToUpload = category.items.some(item => item.photoFile);
    
    if (!hasPhotosToUpload) {
      toast({
        title: 'No Photos to Upload',
        description: 'All photos for this step have been uploaded successfully.',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setIsUploadingCategoryPhotos(true);
    
    const uploadingToast = toast({
      title: 'Retrying Upload...',
      description: `Retrying failed photos for ${category.name}`,
      status: 'info',
      duration: null,
      isClosable: false,
    });

    try {
      const uploadResult = await uploadCategoryPhotos(checklistData?.customId || '', selectedCompany?.companyId || '', selectedVehicle.plateNo, category);
      
      // Update the categories state
      setCategories(prev =>
        prev.map((cat, index) =>
          index === categoryIndex ? uploadResult.updatedCategory : cat
        )
      );

      // Update upload status using stepIndex (currentStep)
      setUploadStatus(prev => ({
        ...prev,
        [stepIndex]: {
          hasPhotos: uploadResult.totalCount > 0,
          uploadedCount: uploadResult.uploadedCount,
          failedCount: uploadResult.failedCount,
          totalCount: uploadResult.totalCount,
          failedItems: uploadResult.failedItems
        }
      }));

      toast.close(uploadingToast);
      
      if (uploadResult.failedCount > 0) {
        toast({
          title: 'Still Some Failures',
          description: `${uploadResult.uploadedCount} uploaded, ${uploadResult.failedCount} still failed. Check your internet connection.`,
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Upload Successful',
          description: `All photos for ${category.name} uploaded successfully!`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast.close(uploadingToast);
      console.error('Error retrying uploads:', error);
      toast({
        title: 'Retry Failed',
        description: 'Failed to retry uploads. Please check your internet connection.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsUploadingCategoryPhotos(false);
    }
  };

  // Get upload summary for display
  const getUploadSummary = () => {
    const stepsWithIssues = Object.entries(uploadStatus)
      .filter(([_, status]) => status.failedCount > 0)
      .map(([stepIndex, status]) => ({
        stepIndex: parseInt(stepIndex),
        categoryName: categories[parseInt(stepIndex) - 1]?.name || 'Unknown', // -1 because stepIndex is currentStep (1-based)
        ...status
      }));
    
    // Clean up upload status entries that no longer have failures
    const stepsWithIssuesKeys = stepsWithIssues.map(issue => issue.stepIndex);
    const currentUploadStatusKeys = Object.keys(uploadStatus).map(key => parseInt(key));
    const keysToCleanup = currentUploadStatusKeys.filter(key => !stepsWithIssuesKeys.includes(key));
    
    if (keysToCleanup.length > 0) {
      // Clean up entries with no failures in the next render cycle
      setTimeout(() => {
        setUploadStatus(prev => {
          const cleaned = { ...prev };
          keysToCleanup.forEach(key => {
            if (cleaned[key] && cleaned[key].failedCount === 0) {
              delete cleaned[key];
            }
          });
          return cleaned;
        });
      }, 100);
    }
    
    return stepsWithIssues;
  };

  // Navigate to specific step (for fixing upload issues)
  const goToStep = async (stepIndex: number) => {
    // Save current progress before navigating
    await saveCurrentProgress();
    
    setCurrentStep(stepIndex);
    setActiveStep(stepIndex);
    onSummaryClose();
  };

  const handleGeneratePDF = async () => {
    // Check if vehicle is selected
    if (!selectedVehicle) {
      toast({
        title: 'Vehicle Required',
        description: 'Please select a vehicle before exporting.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      const pdfParams: PDFGenerationParams = {
        plateNumber: selectedVehicle?.plateNo || 'N/A',
        companyName: selectedCompany?.companyName || 'N/A',
        categories,
        totalRequired,
        completedRequired,
        isFullyComplete: Boolean(isFullyComplete),
        overallStatus: overallStatus === '' ? undefined : overallStatus as 'pass' | 'fail',
        inspectionSummary,
        signatureDataURL,
        registrationDate: formatDateTimeDisplay(selectedVehicle?.lastRegistration),
        inspectorName: userProfile?.email || 'Unknown',
        checklistType: 'Pre-Departure Checklist',
        updated_at: checklistData?.updated_at
      };

      const fileName = await generatePDFSummary(pdfParams);

      toast({
        title: 'PDF Generated Successfully',
        description: `Checklist exported as ${fileName}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'PDF Generation Failed',
        description: 'There was an error generating the PDF. Please try again.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  return (
        <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <PageHeader
        title="Pre-Departure Checklist"
        subtitle={`Step ${currentStep + 1} of ${categories.length + 1}: ${currentStep === 0 ? 'Vehicle Selection' : getCurrentCategory()?.name}`}
        icon="🚗"
        onBack={onBack}
      />

      {/* Main Content */}
      <Container maxW="6xl" py={8} px={4} ref={mainContentRef}>
        <VStack spacing={6} align="stretch">
          
          {/* Step 0: Vehicle Selection */}
          {currentStep === 0 && (
            <>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="lg" fontWeight="semibold">
                      Vehicle Selection
                    </Text>
                    
                    {/* Search Interface */}
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                        Search Vehicle by Plate Number
                      </Text>
                      <HStack spacing={3}>
                        <Input
                          placeholder="min 3 letters"
                          value={vehicleSearchQuery}
                          onChange={(e) => setVehicleSearchQuery(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && vehicleSearchQuery.trim().length >= 3 && handleVehicleSearch()}
                          bg="white"
                          borderColor="gray.300"
                        />
                        <Button
                          onClick={handleVehicleSearch}
                          colorScheme="blue"
                          isLoading={isSearchingVehicles}
                          loadingText="..."
                          disabled={vehicleSearchQuery.trim().length < 3 || isSearchingVehicles}
                          minW="125px"
                          w="125px"
                        >
                          Search
                        </Button>
                      </HStack>
                      {vehicleSearchQuery.trim().length > 0 && vehicleSearchQuery.trim().length < 3 && (
                        <Text fontSize="xs" color="orange.500" mt={2}>
                          Please enter at least 3 characters to search
                        </Text>
                      )}
                    </Box>

                    {/* Search Results Table */}
                    {hasSearched && (
                      <Box>
                        {searchResults.length > 0 ? (
                          <VStack spacing={4} align="stretch">
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                              Search Results ({searchResults.length} vehicle{searchResults.length !== 1 ? 's' : ''} found)
                            </Text>
                            <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                              <Table variant="simple">
                                <Thead bg="gray.50">
                                  <Tr>
                                    <Th>Select</Th>
                                    <Th>Plate Number</Th>
                                    <Th>Registration Date</Th>
                                    <Th>Status</Th>
                                    <Th>Registration Status</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {searchResults.map((vehicle) => {
                                    const registrationStatus = getRegistrationStatus(vehicle.registrationToExpire);
                                    const isSelected = selectedVehicle?.id === vehicle.id;
                                    return (
                                      <Tr 
                                        key={vehicle.id}
                                        bg={isSelected ? "blue.50" : "white"}
                                        _hover={{ bg: isSelected ? "blue.100" : "gray.50" }}
                                        cursor="pointer"
                                        onClick={() => handleVehicleSelect(vehicle)}
                                      >
                                        <Td>
                                          <Checkbox 
                                            isChecked={isSelected}
                                            onChange={() => handleVehicleSelect(vehicle)}
                                          />
                                        </Td>
                                        <Td fontWeight={isSelected ? "bold" : "normal"}>
                                          {vehicle.plateNo}
                                        </Td>
                                        <Td>
                                          {formatDateTimeDisplay(vehicle.lastRegistration)}
                                        </Td>
                                        <Td>
                                          <Badge colorScheme={vehicle.active ? 'green' : 'red'}>
                                            {vehicle.active ? 'Active' : 'Inactive'}
                                          </Badge>
                                        </Td>
                                        <Td>
                                          <Text 
                                            fontSize="sm" 
                                            fontWeight="semibold"
                                            color={registrationStatus.isExpired ? 'red.500' : 
                                                   registrationStatus.daysUntilExpiry <= 30 ? 'orange.500' : 'green.500'}
                                          >
                                            {registrationStatus.statusText}
                                          </Text>
                                          {registrationStatus.isExpired && (
                                            <Text fontSize="xs" color="red.500" fontWeight="bold">
                                              REGISTRATION EXPIRED
                                            </Text>
                                          )}
                                        </Td>
                                      </Tr>
                                    );
                                  })}
                                </Tbody>
                              </Table>
                            </Box>
                          </VStack>
                        ) : (
                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <Text fontSize="sm">
                              No vehicles found matching "{vehicleSearchQuery}". Try a different search term.
                            </Text>
                          </Alert>
                        )}
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              {/* Selected Vehicle Details */}
              {selectedVehicle && (
                <Card bg="blue.50" borderColor="blue.300">
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between">
                        <Text fontSize="lg" fontWeight="semibold" color="blue.700">
                          Selected Vehicle
                        </Text>
                        <Button size="sm" variant="ghost" onClick={clearVehicleSelection}>
                          Clear Selection
                        </Button>
                      </HStack>
                      
                      <HStack spacing={6} align="start">
                        {/* Vehicle Image */}
                        {selectedVehicle.image && (
                          <Box flexShrink={0}>
                            <Image
                              src={selectedVehicle.image}
                              alt={`Vehicle ${selectedVehicle.plateNo}`}
                              boxSize="150px"
                              objectFit="cover"
                              borderRadius="md"
                              border="2px solid"
                              borderColor="blue.300"
                              fallback={
                                <Box
                                  boxSize="150px"
                                  bg="gray.100"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  borderRadius="md"
                                  border="2px solid"
                                  borderColor="blue.300"
                                >
                                  <Text color="gray.500" fontSize="sm">No Image</Text>
                                </Box>
                              }
                            />
                          </Box>
                        )}
                        
                        {/* Vehicle Details */}
                        <VStack spacing={3} align="start" flex={1}>
                          <Text fontSize="xl" fontWeight="bold">
                            {selectedVehicle.plateNo}
                          </Text>
                          <Text fontSize="md" color="gray.600">
                            Last Registration: {formatDateTimeDisplay(selectedVehicle.lastRegistration)}
                          </Text>
                          
                          {(() => {
                            const registrationStatus = getRegistrationStatus(selectedVehicle.registrationToExpire);
                            return (
                              <Text 
                                fontSize="md" 
                                fontWeight="semibold"
                                color={registrationStatus.isExpired ? 'red.500' : 
                                       registrationStatus.daysUntilExpiry <= 30 ? 'orange.500' : 'green.500'}
                              >
                                {registrationStatus.statusText}
                              </Text>
                            );
                          })()}
                          
                          <Badge colorScheme={selectedVehicle.active ? 'green' : 'red'} size="lg">
                            {selectedVehicle.active ? 'Active' : 'Inactive'}
                          </Badge>
                          
                          {getRegistrationStatus(selectedVehicle.registrationToExpire).isExpired && (
                            <Alert status="error" size="sm" borderRadius="md">
                              <AlertIcon />
                              <Text fontSize="sm" fontWeight="bold">Registration Expired</Text>
                            </Alert>
                          )}
                        </VStack>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </>
          )}

          {/* Steps 1+: Checklist Content */}
          {currentStep > 0 && (
            <>
              {/* Progress Stepper */}
              <Card bg={cardBg} borderColor={borderColor}>
                <CardBody>
                  <Box overflowX="auto" pb={2}>
                    <Stepper 
                      index={activeStep - 1} 
                      orientation="horizontal" 
                      size={{ base: "sm", md: "md" }}
                      minW={{ base: "600px", md: "auto" }}
                    >
                      {categories.map((category, index) => {
                        const stepUploadStatus = uploadStatus[index + 1]; // +1 because step 0 is vehicle selection
                        const hasUploadIssues = stepUploadStatus?.failedCount > 0;
                    const hasPhotos = stepUploadStatus?.hasPhotos;
                    
                    return (
                      <Step key={category.id}>
                        <StepIndicator 
                          flexShrink={0}
                          cursor="pointer"
                          onClick={() => goToStep(index)}
                          _hover={{ transform: "scale(1.1)" }}
                          transition="transform 0.2s"
                        >
                          <StepStatus
                            complete={
                              hasUploadIssues ? (
                                <Box color="orange.500" fontSize="sm">⚠️</Box>
                              ) : (
                                <StepIcon />
                              )
                            }
                            incomplete={<StepNumber />}
                            active={<StepNumber />}
                          />
                        </StepIndicator>
                        <Box 
                          flexShrink="0" 
                          minW={{ base: "60px", md: "auto" }} 
                          maxW="80px"
                          cursor="pointer"
                          onClick={() => goToStep(index)}
                          _hover={{ transform: "scale(1.05)" }}
                          transition="transform 0.2s"
                        >
                          <StepTitle 
                            fontSize={{ base: "2xs", md: "xs" }}
                            noOfLines={2}
                            textAlign="center"
                            lineHeight="tight"
                            color={hasUploadIssues ? "orange.500" : "inherit"}
                          >
                            {category.name}
                          </StepTitle>
                          {hasPhotos && (
                            <Text 
                              fontSize="2xs" 
                              color={hasUploadIssues ? "orange.500" : "green.500"}
                              textAlign="center"
                              mt={1}
                            >
                              {hasUploadIssues 
                                ? `${stepUploadStatus.failedCount} failed`
                                : `${stepUploadStatus.uploadedCount} uploaded`
                              }
                            </Text>
                          )}
                        </Box>
                        <StepSeparator flexShrink={0} />
                      </Step>
                    );
                      })}
                    </Stepper>
                  </Box>
                </CardBody>
              </Card>

              {/* Loading State */}
              {isLoadingTemplate && (
                <Card bg={cardBg} borderColor={borderColor}>
                  <CardBody>
                    <VStack spacing={4} align="center" py={8}>
                      <Text fontSize="lg" fontWeight="semibold">Loading Checklist Template...</Text>
                      <Text fontSize="sm" color="gray.600">Please wait while we load your company's checklist template</Text>
                    </VStack>
                  </CardBody>
                </Card>
              )}

              {/* No Template Found */}
              {!isLoadingTemplate && categories.length === 0 && (
                <Card bg={cardBg} borderColor={borderColor}>
                  <CardBody>
                    <VStack spacing={4} align="center" py={8}>
                      <Text fontSize="lg" fontWeight="semibold" color="orange.600">No Checklist Template Found</Text>
                      <Text fontSize="sm" color="gray.600" textAlign="center">
                        No checklist template is available for your company. Please contact your administrator to set up a checklist template.
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              )}

              {/* Completion Status */}
              {!isLoadingTemplate && categories.length > 0 && !isCurrentCategoryComplete() && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1} flex={1}>
                    <Text fontSize="sm" fontWeight="semibold">
                      Complete the following to continue:
                    </Text>
                    {(() => {
                      const category = getCurrentCategory();
                      if (!category) return null;
                      
                      const requiredItems = category.items.filter(item => item.required);
                      const completedRequired = requiredItems.filter(item => item.status !== undefined && item.status !== '');
                      const incompleteRequired = requiredItems.length - completedRequired.length;
                      
                      const itemsWithRedStatus = category.items.filter(item => item.status === 'red');
                      const redItemsWithoutPhotos = itemsWithRedStatus.filter(item => !item.photo && !item.photoFile && !item.photoStoragePath);
                      
                      return (
                        <VStack align="start" spacing={1} fontSize="xs">
                          {incompleteRequired > 0 && (
                            <Text>• Complete {incompleteRequired} required item{incompleteRequired > 1 ? 's' : ''}</Text>
                          )}
                          {redItemsWithoutPhotos.length > 0 && (
                            <Text color="red.600" fontWeight="semibold">
                              • Add photo{redItemsWithoutPhotos.length > 1 ? 's' : ''} for {redItemsWithoutPhotos.length} item{redItemsWithoutPhotos.length > 1 ? 's' : ''} marked as BAD
                            </Text>
                          )}
                        </VStack>
                      );
                    })()}
                  </VStack>
                </Alert>
              )}

              {/* Current Category */}
              {!isLoadingTemplate && categories.length > 0 && getCurrentCategory() && (
                <Card bg={cardBg} borderColor={borderColor}>
                  <CardBody>
                    <VStack spacing={6} align="stretch">
                      <Flex justify="space-between" align="center">
                        <HStack spacing={3}>
                          <Badge colorScheme={getCurrentCategory()!.color} size="lg">
                            {getCurrentCategory()!.name}
                          </Badge>
                        </HStack>
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme={getCurrentCategory()!.color}
                          leftIcon={<FiPlus />}
                          onClick={() => {
                            setSelectedCategoryId(getCurrentCategory()!.id);
                            onAddItemOpen();
                          }}
                        >
                          Add Item
                        </Button>
                      </Flex>

                      {/* Category Items */}
                      <VStack spacing={6} align="stretch">
                        {getCurrentCategory()!.items.map((item) => (
                          <Box key={item.id} p={4} border="1px" borderColor="gray.200" borderRadius="md">
                            <VStack spacing={4} align="stretch">
                              <Flex justify="space-between" align="center">
                                <HStack spacing={1}>
                                  <Text fontWeight="semibold" color="gray.800">
                                    {item.text}
                                  </Text>
                                  {item.required && (
                                    <Text color="red.500" fontWeight="bold">
                                      (*)
                                    </Text>
                                  )}
                                </HStack>
                                <HStack spacing={2}>
                                  {(item.photo || item.photoStoragePath) && (
                                    <Badge colorScheme="green" size="sm">
                                      <HStack spacing={1}>
                                        <FiImage />
                                        <Text>Photo</Text>
                                      </HStack>
                                    </Badge>
                                  )}
                                  {item.status && (
                                    <Badge 
                                      colorScheme={
                                        item.status === 'green' ? 'green' : 
                                        item.status === 'yellow' ? 'yellow' : 'red'
                                      } 
                                      size="sm"
                                    >
                                      {item.status === 'green' ? 'GOOD' : 
                                       item.status === 'yellow' ? 'CAUTION' : 'BAD'}
                                    </Badge>
                                  )}
                                </HStack>
                              </Flex>

                              {/* Status Radio Buttons */}
                              <Box>
                                <RadioGroup 
                                  value={item.status || ''} 
                                  onChange={(value) => handleStatusChange(getCurrentCategory()!.id, item.id, value as 'green' | 'yellow' | 'red')}
                                >
                                  <Stack direction="row" spacing={4}>
                                    <Radio value="green" colorScheme="green">
                                      <Badge colorScheme="green" variant="solid">Good</Badge>
                                    </Radio>
                                    <Radio value="yellow" colorScheme="yellow">
                                      <Badge colorScheme="yellow" variant="solid">Caution</Badge>
                                    </Radio>
                                    <Radio value="red" colorScheme="red">
                                      <Badge colorScheme="red" variant="solid">Bad</Badge>
                                    </Radio>
                                  </Stack>
                                </RadioGroup>
                              </Box>

                              {/* Photo Required Alert for BAD status */}
                              {item.status === 'red' && !item.photo && !item.photoFile && !item.photoStoragePath && (
                                <Alert status="error" size="sm" borderRadius="md">
                                  <AlertIcon />
                                  <Text fontSize="sm" fontWeight="semibold">
                                    Photo required for items marked as BAD
                                  </Text>
                                </Alert>
                              )}

                              {/* Comment Field */}
                              <Box>
                                <Text fontSize="sm" fontWeight="medium" mb={2}>
                                  Comment
                                </Text>
                                <Textarea
                                  value={item.comment || ''}
                                  onChange={(e) => handleCommentChange(getCurrentCategory()!.id, item.id, e.target.value)}
                                  placeholder="Add any comments or observations..."
                                  size="sm"
                                  resize="vertical"
                                  minH="60px"
                                  bg="white"
                                  borderColor="gray.300"
                                />
                              </Box>

                              {/* Photo Section */}
                              <Flex justify="space-between" align="center">
                                <Button
                                  leftIcon={<FiCamera />}
                                  size="sm"
                                  variant="outline"
                                  colorScheme={item.status === 'red' ? 'red' : getCurrentCategory()!.color}
                                  onClick={() => handlePhotoUpload(getCurrentCategory()!.id, item.id)}
                                  isLoading={isUploadingPhoto && selectedItemId === item.id}
                                  loadingText="Processing..."
                                  borderColor={item.status === 'red' && !item.photo && !item.photoFile && !item.photoStoragePath ? 'red.500' : undefined}
                                  borderWidth={item.status === 'red' && !item.photo && !item.photoFile && !item.photoStoragePath ? '2px' : '1px'}
                                  _hover={{
                                    borderColor: item.status === 'red' ? 'red.600' : undefined,
                                    backgroundColor: item.status === 'red' ? 'red.50' : undefined
                                  }}
                                >
                                  {(item.photo || item.photoStoragePath) ? 'Change Photo' : 'Add Photo'}
                                  {item.status === 'red' && (
                                    <Text as="span" color="red.500" fontWeight="bold" ml={1}>
                                      *
                                    </Text>
                                  )}
                                </Button>
                                {!item.required && (
                                  <IconButton
                                    aria-label="Remove item"
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    variant="outline"
                                    colorScheme="red"
                                    onClick={() => handleRemoveItem(getCurrentCategory()!.id, item.id)}
                                  />
                                )}
                              </Flex>

                              {(item.photo || item.photoStoragePath) && (
                                <PhotoDisplay
                                  photoStoragePath={item.photoStoragePath}
                                  photo={item.photo}
                                  alt="Checklist item photo"
                                  onRemove={() => handleRemovePhoto(getCurrentCategory()!.id, item.id)}
                                />
                              )}
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </>
          )}

          {/* Navigation Buttons */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <VStack spacing={4}>
                {/* Step indicator */}
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Step {currentStep + 1} of {categories.length + 1}
                </Text>
                
                {/* Navigation buttons */}
                <Flex justify="space-between" align="center" w="full">
                  <Button
                    leftIcon={<FiChevronLeft />}
                    variant="outline"
                    onClick={handlePrevStep}
                    disabled={currentStep === 0 || isUploadingCategoryPhotos || isOpeningSummary}
                    isLoading={isUploadingCategoryPhotos}
                  >
                    Previous
                  </Button>

                  <Button
                    rightIcon={currentStep === categories.length ? <FiDownload /> : <FiChevronRight />}
                    colorScheme="blue"
                    onClick={handleNextStep}
                    disabled={!isCurrentCategoryComplete() || isUploadingCategoryPhotos || isOpeningSummary}
                    isLoading={isUploadingCategoryPhotos || (currentStep === categories.length && isOpeningSummary)}
                    loadingText={isOpeningSummary ? "Finalizing" : "Uploading..."}
                  >
                    {currentStep === categories.length ? 'Complete' : 'Next'}
                  </Button>
                </Flex>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>      {/* Summary Modal */}
      <Modal 
        isOpen={isSummaryOpen} 
        onClose={isSubmitting ? () => {} : onSummaryClose} 
        size="full"
        closeOnOverlayClick={!isSubmitting}
        closeOnEsc={!isSubmitting}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={{ base: "lg", md: "xl" }} py={{ base: 4, md: 6 }}>
            Checklist Summary
          </ModalHeader>
          <ModalCloseButton isDisabled={isSubmitting} />
          <ModalBody px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
            <VStack spacing={{ base: 4, md: 6 }} align="stretch">
              
              {/* Status Alert - Show either success or what's missing */}
              {isFullyComplete ? (
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    Checklist completed successfully!
                  </Text>
                </Alert>
              ) : (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={2} flex={1}>
                    <Text fontSize={{ base: "sm", md: "md" }} fontWeight="semibold">
                      Complete the following to submit:
                    </Text>
                    <VStack align="start" spacing={1} fontSize="sm">
                      {!canComplete && (
                        <Text>• Complete all required checklist items</Text>
                      )}
                      {!overallStatus && (
                        <Text>• Provide overall vehicle status assessment</Text>
                      )}
                      {!inspectionSummary.trim() && (
                        <Text>• Add inspection summary</Text>
                      )}
                      {!signatureDataURL && (
                        <Text>• Provide digital signature</Text>
                      )}
                    </VStack>
                  </VStack>
                </Alert>
              )}

              {/* Upload Status Section */}
              {(() => {
                const uploadIssues = getUploadSummary();
                if (uploadIssues.length > 0) {
                  return (
                    <Card borderColor="orange.200" bg="orange.50">
                      <CardBody>
                        <VStack spacing={4} align="stretch">
                          <HStack>
                            <Box color="orange.500" fontSize="lg">⚠️</Box>
                            <Text fontWeight="bold" color="orange.700">
                              Photo Upload Issues
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="orange.600">
                            Some photos failed to upload. You can retry before submitting.
                          </Text>
                          {uploadIssues.map((issue) => (
                            <Flex 
                              key={issue.stepIndex}
                              justify="space-between" 
                              align="center"
                              p={3}
                              bg="white"
                              borderRadius="md"
                              border="1px solid"
                              borderColor="orange.200"
                            >
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="semibold" fontSize="sm">
                                  {issue.categoryName}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  {issue.failedCount} failed, {issue.uploadedCount} successful
                                </Text>
                                <Text fontSize="xs" color="orange.600">
                                  Failed items: {issue.failedItems.join(', ')}
                                </Text>
                              </VStack>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => goToStep(issue.stepIndex)}
                                >
                                  Go Back
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="orange"
                                  variant="outline"
                                  onClick={() => retryFailedUploads(issue.stepIndex)}
                                  isLoading={isUploadingCategoryPhotos}
                                  disabled={isUploadingCategoryPhotos}
                                >
                                  Retry
                                </Button>
                              </HStack>
                            </Flex>
                          ))}
                        </VStack>
                      </CardBody>
                    </Card>
                  );
                }
                return null;
              })()}

              {/* Final Assessment Section */}
              <Card borderColor={isSummaryComplete ? "blue.200" : "orange.200"} bg={isSummaryComplete ? "blue.50" : "orange.50"}>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <HStack>
                      <Box fontSize="lg">{isSummaryComplete ? "📋" : "⚠️"}</Box>
                      <Text fontWeight="bold" color={isSummaryComplete ? "blue.700" : "orange.700"}>
                        Final Assessment {!isSummaryComplete && "(Incomplete)"}
                      </Text>
                    </HStack>
                    
                    {isSummaryComplete ? (
                      <VStack align="stretch" spacing={3}>
                        <Flex align="center" gap={3}>
                          <Text fontWeight="semibold" fontSize="sm">Overall Status:</Text>
                          <Badge 
                            colorScheme={overallStatus === 'pass' ? 'green' : 'red'} 
                            size="md"
                            px={3}
                            py={1}
                          >
                            {overallStatus === 'pass' ? '✅ PASS' : '❌ FAIL'}
                          </Badge>
                        </Flex>
                        
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm" mb={2}>Inspector Summary:</Text>
                          <Box 
                            p={3} 
                            bg="white" 
                            borderRadius="md" 
                            border="1px solid" 
                            borderColor="blue.200"
                          >
                            <Text fontSize="sm" lineHeight="1.5">
                              {inspectionSummary}
                            </Text>
                          </Box>
                        </Box>
                        
                        {signatureDataURL && (
                          <Box>
                            <Text fontWeight="semibold" fontSize="sm" mb={2}>Inspector Signature:</Text>
                            <Box 
                              p={3} 
                              bg="white" 
                              borderRadius="md" 
                              border="1px solid" 
                              borderColor="blue.200"
                              display="flex"
                              justifyContent="center"
                            >
                              <img 
                                src={signatureDataURL} 
                                alt="Inspector Signature" 
                                style={{ 
                                  maxWidth: '300px', 
                                  height: 'auto', 
                                  border: '1px solid #e2e8f0', 
                                  borderRadius: '4px' 
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                      </VStack>
                    ) : (
                      <VStack align="stretch" spacing={3}>
                        <Text fontSize="sm" color="orange.600">
                          Please complete the final assessment to submit your inspection:
                        </Text>
                        <VStack align="start" spacing={2} fontSize="sm">
                          <HStack>
                            <Box color={overallStatus ? "green.500" : "orange.500"} fontSize="lg">
                              {overallStatus ? "✅" : "⭕"}
                            </Box>
                            <Text color={overallStatus ? "gray.600" : "orange.600"}>
                              Overall vehicle status assessment
                            </Text>
                          </HStack>
                          <HStack>
                            <Box color={inspectionSummary.trim() ? "green.500" : "orange.500"} fontSize="lg">
                              {inspectionSummary.trim() ? "✅" : "⭕"}
                            </Box>
                            <Text color={inspectionSummary.trim() ? "gray.600" : "orange.600"}>
                              Inspection summary
                            </Text>
                          </HStack>
                          <HStack>
                            <Box color={signatureDataURL ? "green.500" : "orange.500"} fontSize="lg">
                              {signatureDataURL ? "✅" : "⭕"}
                            </Box>
                            <Text color={signatureDataURL ? "gray.600" : "orange.600"}>
                              Digital signature
                            </Text>
                          </HStack>
                        </VStack>
                        <Button
                          colorScheme="orange"
                          size="sm"
                          onClick={() => {
                            onSummaryClose();
                            onFinalAssessmentOpen();
                          }}
                        >
                          Complete Final Assessment
                        </Button>
                      </VStack>
                    )}
                  </VStack>
                </CardBody>
              </Card>
              
              {categories.map((category) => (
                <Card key={category.id}>
                  <CardBody p={{ base: 4, md: 6 }}>
                    <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                      <Badge colorScheme={category.color} size={{ base: "md", md: "lg" }} alignSelf="start">
                        {category.name}
                      </Badge>
                      {category.items.map((item) => (
                        <Flex 
                          key={item.id} 
                          justify="space-between" 
                          align="center"
                          direction={{ base: "column", sm: "row" }}
                          gap={{ base: 2, sm: 0 }}
                        >
                          <Text 
                            fontSize={{ base: "sm", md: "md" }}
                            flex={1}
                            textAlign={{ base: "center", sm: "left" }}
                          >
                            {item.text}
                            {item.required && (
                              <Text as="span" color="red.500" fontWeight="bold" ml={1}>
                                (*)
                              </Text>
                            )}
                          </Text>
                          <HStack spacing={2} justify={{ base: "center", sm: "flex-end" }}>
                            {/* Debug: Always show status info */}
                            <Badge 
                              colorScheme={
                                item.status === 'green' ? 'green' : 
                                item.status === 'yellow' ? 'yellow' : 
                                item.status === 'red' ? 'red' : 'gray'
                              }
                              size={{ base: "sm", md: "md" }}
                            >
                              {item.status === 'green' ? 'GOOD' : 
                               item.status === 'yellow' ? 'CAUTION' : 
                               item.status === 'red' ? 'BAD' : 
                               item.status ? `STATUS: ${item.status}` : 'NO STATUS'}
                            </Badge>
                            {(item.photo || item.photoStoragePath) && (
                              <Badge colorScheme="blue" size={{ base: "sm", md: "md" }}>
                                Photo
                              </Badge>
                            )}
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          </ModalBody>
          
          {/* Debug info for completion status */}
          {!isFullyComplete && (
            <Box px={{ base: 4, md: 6 }} pb={4}>
              <Alert status="warning" size="sm">
                <AlertIcon />
                <VStack align="start" spacing={1} fontSize="xs">
                  <Text fontWeight="semibold">Missing requirements:</Text>
                  {!canComplete && (
                    <Text>• Complete all required checklist items and add photos for BAD items</Text>
                  )}
                  {!overallStatus && (
                    <Text>• Select overall assessment (Pass/Fail)</Text>
                  )}
                  {!inspectionSummary.trim() && (
                    <Text>• Add inspection summary</Text>
                  )}
                  {!signatureDataURL && (
                    <Text>• Add inspector signature</Text>
                  )}
                </VStack>
              </Alert>
            </Box>
          )}
          
          <ModalFooter 
            px={{ base: 4, md: 6 }} 
            py={{ base: 4, md: 6 }}
            flexDirection={{ base: "column", sm: "row" }}
            gap={{ base: 3, sm: 0 }}
          >
            <Button 
              variant="ghost" 
              mr={{ base: 0, sm: 3 }} 
              onClick={onSummaryClose}
              w={{ base: "full", sm: "auto" }}
              size={{ base: "md", md: "lg" }}
              isDisabled={isSubmitting}
            >
              Close
            </Button>
            <Button 
              colorScheme="blue" 
              leftIcon={<FiDownload />} 
              onClick={handleGeneratePDF}
              w={{ base: "full", sm: "auto" }}
              size={{ base: "md", md: "lg" }}
              fontSize={{ base: "sm", md: "md" }}
              isDisabled={!isFullyComplete || isSubmitting}
            >
              Export PDF
            </Button>
            <Button 
              colorScheme="green" 
              ml={{ base: 0, sm: 3 }} 
              onClick={handleCompleteChecklist}
              w={{ base: "full", sm: "auto" }}
              size={{ base: "md", md: "lg" }}
              fontSize={{ base: "sm", md: "md" }}
              isDisabled={!isFullyComplete || isSubmitting}
              isLoading={isSubmitting}
              loadingText="Submitting..."
            >
              Submit Checklist
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Final Assessment Modal */}
      <Modal isOpen={isFinalAssessmentOpen} onClose={onFinalAssessmentClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="lg" fontWeight="bold">
            Final Inspection Assessment
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontSize="md" fontWeight="semibold" mb={3}>
                  Overall Vehicle Status
                </Text>
                <RadioGroup value={overallStatus} onChange={(value) => setOverallStatus(value as 'pass' | 'fail')}>
                  <VStack align="start" spacing={3}>
                    <Radio value="pass" colorScheme="green">
                      <HStack spacing={2}>
                        <Box color="green.500" fontSize="lg">✅</Box>
                        <Text fontWeight="semibold" color="green.600">PASS</Text>
                        <Text fontSize="sm" color="gray.600">- Vehicle is ready for operation</Text>
                      </HStack>
                    </Radio>
                    <Radio value="fail" colorScheme="red">
                      <HStack spacing={2}>
                        <Box color="red.500" fontSize="lg">❌</Box>
                        <Text fontWeight="semibold" color="red.600">FAIL</Text>
                        <Text fontSize="sm" color="gray.600">- Vehicle needs attention before operation</Text>
                      </HStack>
                    </Radio>
                  </VStack>
                </RadioGroup>
              </Box>

              <Box>
                <Text fontSize="md" fontWeight="semibold" mb={3}>
                  Inspection Summary
                  <Text as="span" color="red.500" fontWeight="bold" ml={1}>*</Text>
                </Text>
                <Textarea
                  placeholder="Provide inspection summary, findings, and recommendations"
                  value={inspectionSummary}
                  onChange={(e) => setInspectionSummary(e.target.value)}
                  rows={2}
                  resize="vertical"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  This summary will be included in the inspection report
                </Text>
              </Box>

              <Box>
                <Text fontSize="md" fontWeight="semibold" mb={3}>
                  Inspector Signature
                  <Text as="span" color="red.500" fontWeight="bold" ml={1}>*</Text>
                </Text>
                <Box 
                  border="2px dashed" 
                  borderColor={signatureDataURL && !isSigningMode ? "green.300" : "gray.300"}
                  borderRadius="md" 
                  p={4}
                  bg={signatureDataURL && !isSigningMode ? "green.50" : "gray.50"}
                >
                  {signatureDataURL && !isSigningMode ? (
                    <VStack spacing={3}>
                      <Text fontSize="sm" color="green.600" fontWeight="medium">
                        ✅ Signature Captured
                      </Text>
                      <img 
                        src={signatureDataURL} 
                        alt="Inspector Signature" 
                        style={{ maxWidth: '100%', height: 'auto', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                      />
                      <Button 
                        size="sm" 
                        variant="outline" 
                        colorScheme="blue"
                        onClick={handleSignatureClear}
                      >
                        Clear & Re-sign
                      </Button>
                    </VStack>
                  ) : (
                    <VStack spacing={3}>
                      <Text fontSize="sm" color="gray.600" textAlign="center">
                        Please sign below to complete your inspection
                      </Text>
                      <Box 
                        border="1px solid" 
                        borderColor="gray.300" 
                        borderRadius="md"
                        bg="white"
                        w="full"
                        h="120px"
                        position="relative"
                        sx={{
                          '& canvas': {
                            touchAction: 'none',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none',
                            WebkitTouchCallout: 'none',
                            WebkitTapHighlightColor: 'transparent',
                            outline: 'none',
                            // Additional iOS fixes
                            WebkitAppearance: 'none',
                            borderRadius: 0, // Prevent iOS rounded corners
                            transform: 'translateZ(0)', // Force hardware acceleration
                            backfaceVisibility: 'hidden' // Prevent flickering
                          }
                        }}
                      >
                        <SignatureCanvas
                          key={isSigningMode ? `signing-${Date.now()}` : 'signed'}
                          ref={signatureRef}
                          canvasProps={{
                            style: {
                              width: '100%',
                              height: '120px',
                              borderRadius: '6px',
                              touchAction: 'none'
                            },
                            tabIndex: -1,
                            'aria-label': 'Signature pad',
                            onFocus: (e) => e.target.blur()
                          }}
                          backgroundColor="white"
                          penColor="black"
                        />
                      </Box>
                      <HStack spacing={3}>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={handleSignatureClear}
                        >
                          Clear
                        </Button>
                        <Button 
                          size="sm" 
                          colorScheme="blue"
                          onClick={handleSignatureSave}
                        >
                          Save Signature
                        </Button>
                      </HStack>
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        Draw your signature with your finger or stylus
                      </Text>
                    </VStack>
                  )}
                </Box>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFinalAssessmentClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="green" 
              onClick={handleFinalAssessmentComplete}
              isDisabled={!overallStatus || !inspectionSummary.trim() || !signatureDataURL}
            >
              Continue to Summary
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add New Item Modal */}
      <Modal isOpen={isAddItemOpen} onClose={onAddItemClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Checklist Item</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Adding to: {getCurrentCategory()?.name}
              </Text>
              <Textarea
                placeholder="Enter checklist item description..."
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                rows={3}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddItemClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleAddNewItem}
              disabled={!newItemText.trim()}
            >
              Add Item
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PerformChecklist;
